import { $Shape } from "utility-types";
import { LazyStore } from "../utils/Store";
import type { CartContextStoreImpl, CartContext } from "./common-cart/CartContextStore";
import deepEquals from "fast-deep-equal/es6";
export interface ICartContextChangeConsumer<Data, SubContext extends $Shape<CartContext>> {
	extractContext: (context: CartContext) => SubContext | null;
	fetchData: (arg0: SubContext) => Promise<Data>;
}
export class CartContextChangeListener<Data, SubContext extends $Shape<CartContext>> extends LazyStore {
	_cartContextConsumer: ICartContextChangeConsumer<Data, SubContext>;
	_state!: {
		subContext: SubContext | null;
		data: Data | null;
	};

	constructor(
		cartContextStore: CartContextStoreImpl,
		cartContextConsumer: ICartContextChangeConsumer<Data, SubContext>,
	) {
		super();
		this._cartContextConsumer = cartContextConsumer;
		this.resetState();

		this.onArm = () => {
			const handleOnce = () => {
				const { country, locale, customerId, cardholderNumber, storeId, fsdAddressId } =
					cartContextStore.getState();
				this.handleCartContextChange({
					country,
					locale,
					customerId,
					cardholderNumber,
					storeId,
					fsdAddressId,
				}).catch((error) => {
					console.error("handleCartContextChange error", error);
				});
			};

			this.listenTo(cartContextStore, handleOnce);
			handleOnce();
		};

		this.onDisarm = () => {
			this.resetState();
		};
	}

	resetState = () => {
		this._state = {
			subContext: null,
			data: null,
		};
		this.trigger();
	};
	handleCartContextChange = async (context: CartContext) => {
		const subContext = this._cartContextConsumer.extractContext(context);

		if (deepEquals(this._state.subContext, subContext)) return;

		if (!subContext) {
			this.resetState();
			return;
		}

		const prevData = this._state.data;
		this._state = {
			subContext,
			data: null,
		};

		if (prevData) {
			// Propagate the reset state in order to not display outdated data until new data is fetched.
			this.trigger();
		}

		const data = await this._cartContextConsumer.fetchData(subContext);

		if (!deepEquals(subContext, this._state.subContext)) {
			// The state changed, while we were fetching.
			return;
		}

		this._state = { ...this._state, data };
		this.trigger();
	};

	// This cannot be an arrow function, because of https://github.com/facebook/flow/issues/4743
	getState(): Data | null {
		return this._state.data;
	}
}
